import { useEffect, useRef } from "react";
import { toast } from "sonner";
import DOMPurify from "dompurify";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import TextAlign from "@tiptap/extension-text-align";
import Placeholder from "@tiptap/extension-placeholder";
import Table from "@tiptap/extension-table";
import TableRow from "@tiptap/extension-table-row";
import TableCell from "@tiptap/extension-table-cell";
import TableHeader from "@tiptap/extension-table-header";
import Image from "@tiptap/extension-image";
import {
  Bold, Italic, Underline as UnderlineIcon, Strikethrough, Heading1, Heading2, Heading3, Pilcrow, List, ListOrdered,
  Quote, Minus, Link as LinkIcon, AlignLeft, AlignCenter, AlignRight, Undo2, Redo2, Table as TableIcon,
  Rows3, Columns3, Trash2, ImagePlus,
} from "lucide-react";

// Mirrors the server allowlist (backend/gov_docs.py POLICY_TAGS); the server re-sanitises on save.
const SANITIZE_OPTS = {
  ALLOWED_TAGS: ["p", "br", "strong", "b", "em", "i", "u", "s", "ul", "ol", "li", "a", "h1", "h2", "h3",
    "blockquote", "hr", "table", "thead", "tbody", "tr", "th", "td", "img"],
  ALLOWED_ATTR: ["href", "colspan", "rowspan", "style", "src", "alt"],
};

export const sanitizePolicyHtml = (html) => DOMPurify.sanitize(html || "", SANITIZE_OPTS);

/** Read-only rendering of a document version. */
export function PolicyContent({ html, testid }) {
  return <div className="policy-content" data-testid={testid} dangerouslySetInnerHTML={{ __html: sanitizePolicyHtml(html) }} />;
}

function Btn({ onClick, active, disabled, title, children }) {
  return (
    <button type="button" title={title} aria-label={title} disabled={disabled}
      onMouseDown={(e) => e.preventDefault()} onClick={onClick}
      className={`h-7 min-w-7 px-1.5 inline-flex items-center justify-center rounded text-slate-600 hover:bg-slate-100 disabled:opacity-30 disabled:hover:bg-transparent ${active ? "bg-[#6b4f8a1f] text-[#6b4f8a]" : ""}`}>
      {children}
    </button>
  );
}

const Sep = () => <span className="w-px h-5 bg-slate-200 mx-1" />;

const MAX_IMAGE_PX = 1600;

// Shrink a picked image to at most 1600px and re-encode it (PNG for diagrams, JPEG for photos)
// so it can be embedded in the document. The server re-checks what it's sent.
function imageToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new window.Image();
    img.onload = () => {
      const scale = Math.min(1, MAX_IMAGE_PX / Math.max(img.width, img.height));
      const c = document.createElement("canvas");
      c.width = Math.round(img.width * scale); c.height = Math.round(img.height * scale);
      c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
      URL.revokeObjectURL(url);
      resolve(file.type === "image/jpeg" ? c.toDataURL("image/jpeg", 0.85) : c.toDataURL("image/png"));
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("unreadable")); };
    img.src = url;
  });
}

/**
 * Rich-text editor for policy / company document drafts. Emits sanitised HTML.
 * `resetKey` forces the content to be replaced from `value` (e.g. after a Word import or reload);
 * otherwise the editor owns its content while typing.
 */
// How many images / tables some HTML has, to detect the editor dropping content it can't represent.
const countOf = (html, tag) => (String(html || "").match(new RegExp(`<${tag}[\\s>]`, "gi")) || []).length;
export function lostContent(sourceHtml, editorHtml) {
  return ["img", "table"].filter((t) => countOf(editorHtml, t) < countOf(sourceHtml, t));
}

export default function PolicyEditor({ value, onChange, resetKey, editable = true, onContentLoss,
  placeholder = "Start writing, or import a Word document…" }) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] }, code: false, codeBlock: false }),
      Underline,
      Link.configure({ openOnClick: false, autolink: true, protocols: ["mailto", "tel"],
        HTMLAttributes: { rel: null, target: null } }),
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      Placeholder.configure({ placeholder }),
      Table.configure({ resizable: false }),
      TableRow, TableHeader, TableCell,
      // inline: Word places pictures inside paragraphs (even inside bold text); block-only
      // images would be silently dropped from imported documents.
      Image.configure({ allowBase64: true, inline: true }),
    ],
    content: sanitizePolicyHtml(value),
    editable,
    onUpdate: ({ editor: ed }) => onChange && onChange(ed.isEmpty ? "" : ed.getHTML()),
    onCreate: ({ editor: ed }) => {
      const lost = lostContent(sanitizePolicyHtml(value), ed.getHTML());
      if (lost.length && onContentLoss) onContentLoss(lost);
    },
  });

  useEffect(() => {
    if (editor && resetKey !== undefined) {
      const clean = sanitizePolicyHtml(value);
      editor.commands.setContent(clean, false);
      const lost = lostContent(clean, editor.getHTML());
      if (lost.length && onContentLoss) onContentLoss(lost);
    }
    // Only on an explicit reset — never while the user is typing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor, resetKey]);

  const fileRef = useRef(null);
  // emitUpdate=false: toggling editability isn't an edit (it would otherwise trigger an autosave).
  useEffect(() => { if (editor) editor.setEditable(editable, false); }, [editor, editable]);

  if (!editor) return null;
  const c = () => editor.chain().focus();
  const inTable = editor.isActive("table");

  const addImage = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!/^image\/(png|jpeg|gif|webp)$/.test(file.type)) { toast.error("Choose a PNG, JPEG, GIF or WebP image."); return; }
    try {
      const src = await imageToDataUrl(file);
      if (src.length > 2.8 * 1024 * 1024) { toast.error("That image is too large. Use a smaller image."); return; }
      editor.chain().focus().setImage({ src, alt: file.name.replace(/\.[^.]+$/, "") }).run();
    } catch { toast.error("Couldn't read that image."); }
  };

  const setLink = () => {
    const prev = editor.getAttributes("link").href || "https://";
    const url = window.prompt("Link address (https://, mailto: or tel:)", prev);
    if (url === null) return;
    if (!url.trim() || url.trim() === "https://") { c().extendMarkRange("link").unsetLink().run(); return; }
    if (!/^(https:\/\/|mailto:|tel:)/i.test(url.trim())) { window.alert("Links must start with https://, mailto: or tel:"); return; }
    c().extendMarkRange("link").setLink({ href: url.trim() }).run();
  };

  return (
    <div className="policy-editor rounded-xl border border-slate-200 bg-white" data-testid="policy-editor">
      {editable && <div className="sticky top-0 z-10 flex flex-wrap items-center gap-0.5 border-b border-slate-200 bg-white/95 backdrop-blur px-2 py-1.5 rounded-t-xl">
        <Btn title="Undo" onClick={() => c().undo().run()} disabled={!editor.can().undo()}><Undo2 className="h-4 w-4" /></Btn>
        <Btn title="Redo" onClick={() => c().redo().run()} disabled={!editor.can().redo()}><Redo2 className="h-4 w-4" /></Btn>
        <Sep />
        <Btn title="Paragraph" active={editor.isActive("paragraph")} onClick={() => c().setParagraph().run()}><Pilcrow className="h-4 w-4" /></Btn>
        <Btn title="Heading 1" active={editor.isActive("heading", { level: 1 })} onClick={() => c().toggleHeading({ level: 1 }).run()}><Heading1 className="h-4 w-4" /></Btn>
        <Btn title="Heading 2" active={editor.isActive("heading", { level: 2 })} onClick={() => c().toggleHeading({ level: 2 }).run()}><Heading2 className="h-4 w-4" /></Btn>
        <Btn title="Heading 3" active={editor.isActive("heading", { level: 3 })} onClick={() => c().toggleHeading({ level: 3 }).run()}><Heading3 className="h-4 w-4" /></Btn>
        <Sep />
        <Btn title="Bold" active={editor.isActive("bold")} onClick={() => c().toggleBold().run()}><Bold className="h-4 w-4" /></Btn>
        <Btn title="Italic" active={editor.isActive("italic")} onClick={() => c().toggleItalic().run()}><Italic className="h-4 w-4" /></Btn>
        <Btn title="Underline" active={editor.isActive("underline")} onClick={() => c().toggleUnderline().run()}><UnderlineIcon className="h-4 w-4" /></Btn>
        <Btn title="Strikethrough" active={editor.isActive("strike")} onClick={() => c().toggleStrike().run()}><Strikethrough className="h-4 w-4" /></Btn>
        <Btn title="Link" active={editor.isActive("link")} onClick={setLink}><LinkIcon className="h-4 w-4" /></Btn>
        <Sep />
        <Btn title="Bulleted list" active={editor.isActive("bulletList")} onClick={() => c().toggleBulletList().run()}><List className="h-4 w-4" /></Btn>
        <Btn title="Numbered list" active={editor.isActive("orderedList")} onClick={() => c().toggleOrderedList().run()}><ListOrdered className="h-4 w-4" /></Btn>
        <Btn title="Quote" active={editor.isActive("blockquote")} onClick={() => c().toggleBlockquote().run()}><Quote className="h-4 w-4" /></Btn>
        <Btn title="Divider" onClick={() => c().setHorizontalRule().run()}><Minus className="h-4 w-4" /></Btn>
        <Sep />
        <Btn title="Align left" active={editor.isActive({ textAlign: "left" })} onClick={() => c().setTextAlign("left").run()}><AlignLeft className="h-4 w-4" /></Btn>
        <Btn title="Align centre" active={editor.isActive({ textAlign: "center" })} onClick={() => c().setTextAlign("center").run()}><AlignCenter className="h-4 w-4" /></Btn>
        <Btn title="Align right" active={editor.isActive({ textAlign: "right" })} onClick={() => c().setTextAlign("right").run()}><AlignRight className="h-4 w-4" /></Btn>
        <Sep />
        <Btn title="Insert image" onClick={() => fileRef.current?.click()}><ImagePlus className="h-4 w-4" /></Btn>
        <Btn title="Insert table" onClick={() => c().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}><TableIcon className="h-4 w-4" /></Btn>
        {inTable && (
          <>
            <Btn title="Add row below" onClick={() => c().addRowAfter().run()}><Rows3 className="h-4 w-4" /></Btn>
            <Btn title="Add column right" onClick={() => c().addColumnAfter().run()}><Columns3 className="h-4 w-4" /></Btn>
            <Btn title="Delete row" onClick={() => c().deleteRow().run()}><span className="text-[10px] font-semibold">−Row</span></Btn>
            <Btn title="Delete column" onClick={() => c().deleteColumn().run()}><span className="text-[10px] font-semibold">−Col</span></Btn>
            <Btn title="Delete table" onClick={() => c().deleteTable().run()}><Trash2 className="h-4 w-4" /></Btn>
          </>
        )}
      </div>}
      <EditorContent editor={editor} className="policy-content px-6 py-5" />
      <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/gif,image/webp" className="hidden" onChange={addImage} />
    </div>
  );
}
